import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { 
  Leaf, 
  Target, 
  Users, 
  Compass, 
  ArrowRight, 
  Sparkles,
  CheckCircle2,
  TrendingUp,
  Heart
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const coachingCategories = [
    { name: "Leadership Resilience", icon: Heart },
    { name: "Sabbatical Planning", icon: Leaf },
    { name: "Succession & Transition", icon: TrendingUp },
    { name: "Church Planting", icon: Sparkles },
    { name: "Staff & Team Leadership", icon: Users },
    { name: "Non-Profit Leadership", icon: Compass }
  ];

  const features = [
    {
      title: "Ministry-Focused Matching",
      description: "Our assessment identifies your unique ministry context, challenges, and goals to connect you with coaches who've walked similar paths.",
      icon: Target
    },
    {
      title: "Experienced Ministry Leaders",
      description: "Every coach in our network has served in pastoral or ministry leadership and understands the unique demands you face.",
      icon: CheckCircle2
    },
    {
      title: "Holistic Soul Care",
      description: "We consider your spiritual health, leadership style, and personal circumstances to find a coach who can support your whole journey.",
      icon: Compass
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] relative overflow-hidden">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#78716C] hover:text-[#2C332B] transition-colors font-medium">Features</a>
            <a href="#categories" className="text-[#78716C] hover:text-[#2C332B] transition-colors font-medium">Categories</a>
            <a href="#how-it-works" className="text-[#78716C] hover:text-[#2C332B] transition-colors font-medium">How It Works</a>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button 
                onClick={() => navigate("/dashboard")}
                className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82] text-white px-6"
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/coach-login")}
                  className="text-[#78716C] font-medium hidden md:inline-flex"
                  data-testid="coach-login-btn"
                >
                  Coach Login
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/login")}
                  className="text-[#2C332B] font-medium"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate("/register")}
                  className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82] text-white px-6"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1505209097149-d3c1c703071a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxzcHJpbmclMjBuYXR1cmUlMjBncm93dGglMjBzcHJvdXQlMjBzZWVkbGluZyUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MDc1MzQ4Mnww&ixlib=rb-4.1.0&q=85"
            alt="Growth"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="hero-overlay absolute inset-0"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2C332B] leading-tight mb-6">
                Real Wisdom. Real Coaching.
                <span className="block text-[#7C9A92]">AI Matched.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#78716C] leading-relaxed mb-8 max-w-xl">
                Connect with experienced ministry coaches who understand the unique challenges of pastoral leadership, church planting, and Christian non-profit work.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate(user ? "/survey" : "/register")}
                  className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82] text-white px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
                  data-testid="hero-cta-btn"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/coaches")}
                  className="rounded-full border-2 border-stone-200 text-[#2C332B] px-8 py-6 text-lg font-medium hover:bg-[#F5F1E8]"
                  data-testid="browse-coaches-btn"
                >
                  Browse Coaches
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1653762370785-24d6f8835044?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHx0d28lMjBwZW9wbGUlMjB0YWxraW5nJTIwY29mZmVlJTIwc2hvcCUyMHdhcm0lMjBmcmllbmRseSUyMG1lZXRpbmd8ZW58MHx8fHwxNzcwODM4ODUxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Coaching Session"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C332B]/20 to-transparent"></div>
              </div>
              
              {/* Floating Stats Card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-stone-100"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E09F7D]/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#E09F7D]" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#2C332B]">500+</p>
                    <p className="text-sm text-[#78716C]">Coaches Available</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl md:text-5xl font-normal text-[#2C332B] mb-4">
              Why Ministry Leaders Choose CoachBridge
            </h2>
            <p className="text-lg text-[#78716C] max-w-2xl mx-auto mb-6">
              We've designed a thoughtful approach to help pastors, ministry leaders, and non-profit executives find the coaching relationship that will sustain and strengthen their calling.
            </p>
            <p className="text-base italic text-[#7C9A92] max-w-xl mx-auto border-l-4 border-[#7C9A92]/30 pl-4 text-left">
              "Leadership is a lonely place, but you don't have to do it alone."
              <span className="block text-sm mt-1 not-italic text-[#78716C]">— Pastor Jeff Urke, Founder of CoachBridge</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#7C9A92]" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-[#2C332B] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#78716C] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaching Categories */}
      <section id="categories" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-serif text-4xl md:text-5xl font-normal text-[#2C332B] mb-4">
              Coaching for Every Season of Ministry
            </h2>
            <p className="text-lg text-[#78716C] max-w-2xl">
              Find expertise across every area of pastoral and ministry leadership.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coachingCategories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-4 p-6 rounded-2xl border border-stone-100 bg-white"
                data-testid={`category-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#F5F1E8] flex items-center justify-center">
                  <category.icon className="w-6 h-6 text-[#7C9A92]" />
                </div>
                <span className="font-medium text-[#2C332B]">
                  {category.name}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl md:text-5xl font-normal text-[#2C332B] mb-4">
              How It Works
            </h2>
            <p className="text-lg text-[#78716C] max-w-2xl mx-auto">
              Three simple steps to find your perfect coaching match
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Share Your Story", desc: "Answer questions about your ministry context, challenges, and what you're looking for in a coach." },
              { step: "02", title: "Get Matched", desc: "Our proprietary matching system finds coaches who've navigated similar ministry seasons." },
              { step: "03", title: "Grow Together", desc: "Connect with your matched coach and begin a journey toward healthier, more effective leadership." }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
                data-testid={`how-it-works-${index}`}
              >
                <span className="text-8xl font-serif font-light text-[#7C9A92]/20 absolute -top-4 -left-2">
                  {item.step}
                </span>
                <div className="pt-16 pl-4">
                  <h3 className="font-serif text-2xl font-medium text-[#2C332B] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-[#78716C] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-12 border border-stone-100 shadow-lg"
          >
            <Leaf className="w-16 h-16 text-[#7C9A92] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#2C332B] mb-4">
              Ready to Invest in Your Leadership?
            </h2>
            <p className="text-lg text-[#78716C] mb-8 max-w-xl mx-auto">
              Take the first step toward healthier ministry. Find a coach who understands your calling and can help you flourish.
            </p>
            <Button 
              onClick={() => navigate(user ? "/survey" : "/register")}
              className="rounded-full bg-[#E09F7D] hover:bg-[#D08F6D] text-white px-10 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
              data-testid="cta-start-btn"
            >
              Begin Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 px-6 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-3xl text-[#2C332B] italic leading-relaxed mb-6">
            "Projects of personal transformation rarely if ever succeed by accident, drift, or imposition."
          </p>
          <p className="text-[#78716C] font-medium">— Dallas Willard</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-stone-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6 text-[#7C9A92]" />
              <span className="font-serif text-xl font-medium text-[#2C332B]">CoachBridge</span>
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate("/apply-to-coach")}
                className="text-[#78716C] hover:text-[#7C9A92] font-medium transition-colors"
              >
                Apply to Coach
              </button>
              <button 
                onClick={() => navigate("/coaches")}
                className="text-[#78716C] hover:text-[#7C9A92] font-medium transition-colors"
              >
                Browse Coaches
              </button>
            </div>
          </div>
          <p className="text-[#78716C] text-sm text-center">
            &copy; {new Date().getFullYear()} CoachBridge. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
